import React, { useState } from 'react';
import crm, { PRODUCT_CATALOG } from './api';

// Quick-enrich popup for the employee "My Leads" page.
//
// A lead arrives from a card tap / WhatsApp touchpoint carrying only the basics
// (name, business, WhatsApp number). After the rep actually talks to the customer
// at a conference or meeting, they open this popup to record what was discussed —
// which product lines came up and any notes — without leaving the leads list.
//
// The full 360° lead view still lives one click away via "View full details".
//
// Props:
//   lead     the lead object (used to prefill)
//   onClose  close without navigating
//   onSaved  called after a successful save (parent reloads the list)
//   onView   open the full LeadDetail for this lead
export default function LeadCaptureModal({ lead, onClose, onSaved, onView }) {
  const seededProducts = Array.isArray(lead.products) && lead.products.length
    ? lead.products
    : (PRODUCT_CATALOG.includes(lead.interest) ? [lead.interest] : []);

  const [f, setF] = useState({
    name: lead.name || '',
    company: lead.company || '',      // business name
    phone: lead.phone || '',          // WhatsApp number
    city: lead.city || '',
    message: lead.message || '',      // remarks captured at the WhatsApp touchpoint
    discussion: lead.discussion || '',// what was discussed in person
  });
  const [products, setProducts] = useState(seededProducts);
  const [productQ, setProductQ] = useState('');
  const [state, setState] = useState('idle'); // idle | saving | error

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleProduct = (p) =>
    setProducts((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const save = () => {
    if (!f.name.trim()) { alert('Please enter the customer name.'); return; }
    setState('saving');
    crm.put(`/leads/${lead.id}`, {
      name: f.name.trim(),
      company: f.company.trim() || null,
      phone: f.phone.trim() || null,
      city: f.city.trim() || null,
      message: f.message.trim() || null,
      discussion: f.discussion.trim() || null,
      products,
    })
      .then(() => { onSaved && onSaved(); onClose(); })
      .catch(() => setState('error'));
  };

  const field = 'w-full px-3.5 py-2.5 border border-[var(--border)] rounded-lg text-sm bg-[var(--card)] text-[var(--text)] focus:ring-2 focus:ring-teal-500 focus:border-transparent';
  const label = 'text-[12px] font-semibold text-[var(--muted)] mb-1 block';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-[var(--card)] rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-[var(--border)] sticky top-0 bg-[var(--card)] rounded-t-2xl">
          <div>
            <h3 className="text-lg font-extrabold text-[var(--text)]">Update Lead</h3>
            <p className="text-[13px] text-[var(--muted)]">Add what the customer discussed with you.</p>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Contact — from the WhatsApp touchpoint */}
          <div className="space-y-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">From WhatsApp / touchpoint</div>
            <div>
              <label className={label}>Name *</label>
              <input value={f.name} onChange={set('name')} className={field} placeholder="Customer name" />
            </div>
            <div>
              <label className={label}>Business name</label>
              <input value={f.company} onChange={set('company')} className={field} placeholder="Clinic / hospital / company" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>WhatsApp number</label>
                <input value={f.phone} onChange={set('phone')} className={field} placeholder="9xxxxxxxxx" inputMode="tel" />
              </div>
              <div>
                <label className={label}>City <span className="font-normal normal-case">(optional)</span></label>
                <input value={f.city} onChange={set('city')} className={field} placeholder="City" />
              </div>
            </div>
            <div>
              <label className={label}>Remarks <span className="font-normal normal-case">(optional)</span></label>
              <input value={f.message} onChange={set('message')} className={field} placeholder="Anything noted at first contact" />
            </div>
          </div>

          {/* Products of interest — searchable, scrollable multi-select */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-2">
              Interested products <span className="normal-case font-normal text-[var(--muted)]">— search &amp; select one or more</span>
            </div>

            {/* selected chips (removable) — always visible even while filtering/scrolling */}
            {products.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {products.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full bg-teal-500/10 text-teal-700 border border-teal-500/30">
                    {p}
                    <button onClick={() => toggleProduct(p)} className="text-teal-700/70 hover:text-teal-900 leading-none text-[14px]">×</button>
                  </span>
                ))}
              </div>
            )}

            <input value={productQ} onChange={(e) => setProductQ(e.target.value)} placeholder="Type to filter products…"
              className={field + ' mb-2'} />

            <div className="max-h-52 overflow-y-auto rounded-lg border border-[var(--border)] divide-y divide-[var(--border)]">
              {(() => {
                const list = PRODUCT_CATALOG.filter((p) => p.toLowerCase().includes(productQ.trim().toLowerCase()));
                if (list.length === 0) return <div className="px-3 py-3 text-[13px] text-[var(--muted)]">No products match “{productQ}”.</div>;
                return list.map((p) => {
                  const on = products.includes(p);
                  return (
                    <label key={p}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-[13px] transition-colors ${on ? 'bg-teal-500/10 text-[var(--text)] font-semibold' : 'text-[var(--muted)] hover:bg-[var(--soft)]'}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleProduct(p)} className="accent-teal-600 flex-none" />
                      <span>{p}</span>
                    </label>
                  );
                });
              })()}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-1">{products.length} selected</div>
          </div>

          {/* Discussion notes */}
          <div>
            <label className={label}>Discussion details / employee remarks</label>
            <textarea value={f.discussion} onChange={set('discussion')} rows={4} className={field}
              placeholder="What did you discuss? Requirements, budget, next steps, who you met…" />
          </div>

          {state === 'error' && <p className="text-red-600 text-sm">Could not save. Please try again.</p>}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-[var(--border)] sticky bottom-0 bg-[var(--card)] rounded-b-2xl">
          <button onClick={() => onView && onView(lead.id)}
            className="text-[13px] font-semibold text-[var(--acc2)] hover:underline">View full details →</button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[var(--border)] text-[13px] font-semibold text-[var(--text)] hover:bg-[var(--soft)]">Cancel</button>
            <button onClick={save} disabled={state === 'saving'}
              className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#1a8046] to-[#0b6e3e] text-white text-[13px] font-bold hover:brightness-105 disabled:opacity-60">
              {state === 'saving' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
